import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';
import { useFormContext, Controller, UseControllerProps } from 'react-hook-form';
import React from 'react';
import { Message } from 'primereact/message';

type Props = Omit<AutoCompleteProps, 'value' | 'onChange' | 'multiple'> & {
  inputName: string;
  inputLabel: string;
  containerClass?: string;
  rules?: UseControllerProps['rules'];
  /**
   * Optional field name to set the ID value when an object is selected.
   * Works with useFieldArray - if inputName is "addresses.0.userAutocomplete"
   * and idFieldName is "userId", it will set "addresses.0.userId"
   * For multiple mode, sets an array of IDs
   */
  idFieldName?: string;
  /** Property name to extract the ID from the selected object (defaults to 'id') */
  idProperty?: string;
  /** Enable multiple selection mode */
  multiple?: boolean;
};

export default function AutoCompleteField({
  inputName,
  inputLabel,
  containerClass = '',
  rules,
  idFieldName,
  idProperty = 'id',
  multiple,
  ...other
}: Props) {
  const { control, setValue } = useFormContext();

  return (
    <div className={`field ${containerClass}`}>
      <label htmlFor={inputName}>{inputLabel}</label>
      <Controller
        name={inputName}
        control={control}
        rules={rules}
        render={({ field: { onChange, value, ...field }, fieldState }) => (
          <React.Fragment>
            <AutoComplete
              id={inputName}
              {...field}
              value={value}
              multiple={multiple}
              onChange={(e) => {
                onChange(e.value);
                // If idFieldName is provided, also set the ID field
                if (idFieldName) {
                  // Handle useFieldArray case - construct the correct ID field name
                  // If inputName contains array notation (e.g., "addresses.0.userAutocomplete")
                  // we need to construct the ID field name accordingly (e.g., "addresses.0.userId")
                  let targetIdFieldName = idFieldName;

                  // Check if inputName contains array index pattern (e.g., "addresses.0.city")
                  const arrayMatch = inputName.match(/^(.+\.\d+)\./);
                  if (arrayMatch) {
                    // Extract the array path (e.g., "addresses.0") and append the ID field name
                    const arrayPath = arrayMatch[1];
                    targetIdFieldName = `${arrayPath}.${idFieldName}`;
                  }

                  // Handle both single and multiple selection modes
                  let selectedIds;
                  if (Array.isArray(e.value)) {
                    // Multiple mode: map array of objects to array of IDs
                    selectedIds = e.value?.map((item) => item?.[idProperty]) ?? [];
                  } else {
                    // Single mode: get single ID or null
                    selectedIds = e.value?.[idProperty] ?? null;
                  }

                  setValue(targetIdFieldName, selectedIds);
                }
              }}
              className={fieldState.error && 'p-invalid'}
              {...other}
            />
            {fieldState.error && (
              <Message id={`error-msg-${inputName}`} severity="error" text={fieldState.error?.message} />
            )}
          </React.Fragment>
        )}
      />
    </div>
  );
}
